import { createContext, useCallback, useContext, useEffect, useMemo, useState, FC, ReactNode } from 'react'
import {
    applyResolvedTheme,
    getSystemPrefersDark,
    getThemePreference,
    resolveTheme,
    setThemePreference as persistThemePreference,
    ResolvedTheme,
    ThemePreference,
} from 'utils/theme'

interface ThemeContextValue {
    preference: ThemePreference
    resolved: ResolvedTheme
    setPreference: (preference: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export const ThemeProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [preference, setPreferenceState] = useState<ThemePreference>(getThemePreference)
    const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark)
    const resolved = resolveTheme(preference, systemPrefersDark)

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        const onChange = () => setSystemPrefersDark(mediaQuery.matches)
        mediaQuery.addEventListener('change', onChange)
        return () => mediaQuery.removeEventListener('change', onChange)
    }, [])

    useEffect(() => {
        applyResolvedTheme(resolved)
    }, [resolved])

    const setPreference = useCallback((next: ThemePreference) => {
        persistThemePreference(next)
        setPreferenceState(next)
    }, [])

    const value = useMemo(() => ({ preference, resolved, setPreference }), [preference, resolved, setPreference])

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    )
}

export const useTheme = () => {
    const context = useContext(ThemeContext)
    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider')
    }
    return context
}

export default useTheme
